import React, { useState, useRef, useEffect, useMemo } from 'react'
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react'
import { DateTime } from 'luxon'

function getBounds (minDate, maxDate) {
  const minDT = minDate ? DateTime.fromISO(minDate).startOf('day') : null
  const maxDT = maxDate ? DateTime.fromISO(maxDate).endOf('day') : null
  return { minDT, maxDT }
}

function monthOverlapsRange (year, month, minDT, maxDT) {
  const start = DateTime.fromObject({ year, month, day: 1 }).startOf('day')
  const end = start.endOf('month')
  if (minDT && end < minDT) return false
  if (maxDT && start > maxDT) return false
  return true
}

function yearsInRange (minDT, maxDT) {
  const now = DateTime.local()
  let yMin = now.year - 25
  let yMax = now.year + 1
  if (minDT) yMin = minDT.year
  if (maxDT) yMax = maxDT.year
  if (yMin > yMax) {
    const t = yMin
    yMin = yMax
    yMax = t
  }
  const years = []
  for (let y = yMin; y <= yMax; y++) years.push(y)
  return years
}

function monthsForYear (year, minDT, maxDT) {
  const list = []
  for (let m = 1; m <= 12; m++) {
    if (monthOverlapsRange(year, m, minDT, maxDT)) list.push(m)
  }
  return list
}

function clampMonthToValidYear (year, preferredMonth, minDT, maxDT) {
  const months = monthsForYear(year, minDT, maxDT)
  if (months.includes(preferredMonth)) return preferredMonth
  return months[0] ?? 1
}

const selectClass =
  'flex-1 min-w-0 max-w-[7.5rem] text-xs rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white py-1.5 pl-1.5 pr-6 outline-none focus:ring-1 focus:ring-blue-500/40'

export default function DatePicker ({
  value, // string 'YYYY-MM-DD'
  onChange, // (val: string) => void
  placeholder = 'Select a date...',
  disabled = false,
  className = '',
  id,
  name,
  minDate, // string 'YYYY-MM-DD'
  maxDate // string 'YYYY-MM-DD'
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [currentMonth, setCurrentMonth] = useState(value ? DateTime.fromISO(value).startOf('month') : DateTime.local().startOf('month'))
  const containerRef = useRef(null)
  const closeTimeoutRef = useRef(null)

  const { minDT, maxDT } = useMemo(() => getBounds(minDate, maxDate), [minDate, maxDate])
  const yearOptions = useMemo(() => yearsInRange(minDT, maxDT), [minDT, maxDT])
  const monthOptions = useMemo(
    () => monthsForYear(currentMonth.year, minDT, maxDT),
    [currentMonth.year, minDT, maxDT]
  )

  const handleClose = () => {
    if (isClosing) return
    setIsClosing(true)
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false)
      setIsClosing(false)
    }, 150)
  }

  const handleOpen = () => {
    if (disabled) return
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current)
    setIsClosing(false)
    setIsOpen(true)
    if (value) setCurrentMonth(DateTime.fromISO(value).startOf('month'))
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        if (isOpen && !isClosing) handleClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, isClosing])

  useEffect(() => {
    const validMonths = monthsForYear(currentMonth.year, minDT, maxDT)
    if (validMonths.length > 0 && !validMonths.includes(currentMonth.month)) {
      setCurrentMonth((prev) => DateTime.fromObject({ year: prev.year, month: validMonths[0], day: 1 }))
    }
  }, [currentMonth.year, currentMonth.month, minDT, maxDT])

  const goPrevMonth = () => {
    const prev = currentMonth.minus({ months: 1 }).startOf('month')
    if (monthOverlapsRange(prev.year, prev.month, minDT, maxDT)) {
      setCurrentMonth(prev)
    }
  }

  const goNextMonth = () => {
    const next = currentMonth.plus({ months: 1 }).startOf('month')
    if (monthOverlapsRange(next.year, next.month, minDT, maxDT)) {
      setCurrentMonth(next)
    }
  }

  const onYearChange = (e) => {
    const year = parseInt(e.target.value, 10)
    const m = clampMonthToValidYear(year, currentMonth.month, minDT, maxDT)
    setCurrentMonth(DateTime.fromObject({ year, month: m, day: 1 }))
  }

  const onMonthChange = (e) => {
    const month = parseInt(e.target.value, 10)
    setCurrentMonth(DateTime.fromObject({ year: currentMonth.year, month, day: 1 }))
  }

  const canGoPrev = useMemo(() => {
    const prev = currentMonth.minus({ months: 1 })
    return monthOverlapsRange(prev.year, prev.month, minDT, maxDT)
  }, [currentMonth, minDT, maxDT])

  const canGoNext = useMemo(() => {
    const next = currentMonth.plus({ months: 1 })
    return monthOverlapsRange(next.year, next.month, minDT, maxDT)
  }, [currentMonth, minDT, maxDT])

  // generate days
  const startDay = currentMonth.startOf('week') // Monday
  const endDay = currentMonth.endOf('month').endOf('week') // Sunday
  const days = []
  let day = startDay
  while (day <= endDay) {
    days.push(day)
    day = day.plus({ days: 1 })
  }

  return (
    <div ref={containerRef} className={`relative w-full text-left ${className}`}>
      <button
        type='button'
        id={id}
        name={name}
        disabled={disabled}
        onClick={() => {
          if (disabled || isClosing) return
          if (isOpen) handleClose()
          else handleOpen()
        }}
        className={`w-full flex items-center justify-between px-3 py-2 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg shadow-sm text-sm outline-none transition-all duration-200 
          ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 text-gray-500 dark:text-gray-400' : 'cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500/20'}`}
      >
        <div className='flex items-center gap-2'>
          <CalendarIcon className='w-4 h-4 text-gray-400 dark:text-gray-500' />
          <span className={`block truncate ${!value ? 'text-gray-400 dark:text-gray-500 dark:text-slate-500' : 'text-gray-900 dark:text-white'}`}>
            {value ? DateTime.fromISO(value).toFormat('MMMM d, yyyy') : placeholder}
          </span>
        </div>
      </button>

      {(isOpen || isClosing) && (
        <div
          className={`absolute z-[100] w-72 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden duration-150 p-3 ${isClosing ? 'animate-out fade-out zoom-out-95 pointer-events-none' : 'animate-in fade-in zoom-in-95'} top-full mt-1`}
        >
          {/* Header: month/year dropdowns + chevrons */}
          <div className='flex items-center gap-1 mb-2'>
            <button
              type='button'
              onClick={goPrevMonth}
              disabled={!canGoPrev}
              className='shrink-0 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-300 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent'
            >
              <ChevronLeft size={16} />
            </button>
            <select
              aria-label='Month'
              value={
                monthOptions.length
                  ? (monthOptions.includes(currentMonth.month) ? currentMonth.month : monthOptions[0])
                  : currentMonth.month
              }
              onChange={onMonthChange}
              className={selectClass}
            >
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {DateTime.fromObject({ year: 2000, month: m, day: 1 }).toFormat('MMMM')}
                </option>
              ))}
            </select>
            <select
              aria-label='Year'
              value={
                yearOptions.length
                  ? (yearOptions.includes(currentMonth.year) ? currentMonth.year : yearOptions[0])
                  : currentMonth.year
              }
              onChange={onYearChange}
              className={selectClass}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <button
              type='button'
              onClick={goNextMonth}
              disabled={!canGoNext}
              className='shrink-0 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-600 dark:text-gray-300 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent dark:disabled:hover:bg-transparent'
            >
              <ChevronRight size={16} />
            </button>
          </div>
          {/* Days of week */}
          <div className='grid grid-cols-7 mb-1'>
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
              <div key={i} className='text-center text-xs font-semibold text-gray-400 dark:text-gray-500 py-1'>{d}</div>
            ))}
          </div>
          {/* Days grid */}
          <div className='grid grid-cols-7 gap-1 flex-1'>
            {days.map(d => {
              const isCurrentMonth = d.hasSame(currentMonth, 'month')
              const isSelected = value && d.hasSame(DateTime.fromISO(value), 'day')
              let isDisabled = false
              if (minDate && d.toISODate() < minDate) isDisabled = true
              if (maxDate && d.toISODate() > maxDate) isDisabled = true

              return (
                <button
                  key={d.toISODate()}
                  type='button'
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (isDisabled) return
                    onChange(d.toISODate())
                    handleClose()
                  }}
                  disabled={isDisabled}
                  className={`
                    w-full h-8 flex items-center justify-center text-sm rounded-md transition-colors
                    ${isDisabled ? 'text-gray-300 dark:text-slate-600 cursor-not-allowed opacity-50 pointer-events-none' : ''}
                    ${!isDisabled && !isSelected && isCurrentMonth ? 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer' : ''}
                    ${!isDisabled && !isSelected && !isCurrentMonth ? 'text-gray-400 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer' : ''}
                    ${!isDisabled && isSelected ? 'bg-ertc-primary text-white font-semibold shadow-sm cursor-pointer' : ''}
                  `}
                >
                  {d.toFormat('d')}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
